/**
 * `/studio/clients/new` — the onboarding wizard.
 *
 * `.docs/20` §B: each step is one action, and **the wizard is a sequence rather than
 * a transaction**. That is not a simplification, it is the design: `clients_create`,
 * `clients_invite_member` and `retainers_start` are three separate commits over three
 * separate connections, and nothing could roll the first back once the third fails.
 *
 * So the wizard is written so that stopping is always safe and never lossy:
 *
 *   - **Every state is a URL.** `?step=` and `?client=` are the whole of it, so a
 *     half-finished account is a link somebody can send, and a reload picks up where
 *     it stopped. Nothing is held in memory between steps; each one re-reads what it
 *     needs (`account.ts`).
 *   - **Abandoning after step one leaves a client with no retainer.** That is a state
 *     the book already renders — MRR `—`, burn `—` (`.docs/20` §Edge cases) — and the
 *     "carry on where you left off" link on this page is how you find it again.
 *   - **Abandoning after step four leaves a live retainer.** `retainers_start` queues
 *     the signup invoice itself; the worker issues it whether or not this tab is
 *     still open, which is why the last step previews rather than sends.
 *
 * A step past the first with no `?client=` is a link somebody edited or a bookmark
 * from before the account existed. It says so and offers the first step, rather than
 * rendering a form that would refuse on submit.
 */

import { z } from 'zod'

import { Button, ButtonRow } from '../../../components/portal/Button.js'
import { Section, Stack } from '../../../components/portal/Layout.js'
import { Region, RegionHead } from '../../../components/portal/Region.js'
import { Notice } from '../../../components/portal/Status.js'
import { Step, Stepper } from '../../../components/portal/Stepper.js'
import { Mono, Muted, Prose, Small } from '../../../components/portal/Text.js'
import { useSearchState } from '../../../search/useSearchState.js'
import { PageTitle } from '../../../shell/PageTitle.js'
import { DetailsStep } from './DetailsStep.js'
import { OwnerStep } from './OwnerStep.js'
import { PackageStep } from './PackageStep.js'
import { PaymentStep } from './PaymentStep.js'
import { STEPS, type StepKey, indexOfStep, needsClient, nextStep } from './steps.js'
import { TermsStep } from './TermsStep.js'

/** Which step is on screen, and the client the earlier steps created. */
export const OnboardingSearchSchema = z.object({
  step: z.enum(['details', 'terms', 'owner', 'package', 'payment']).default('details'),
  client: z.string().optional(),
})

export function OnboardingPage() {
  const [search, setSearch] = useSearchState(OnboardingSearchSchema)
  const step: StepKey = search.step
  const client = search.client
  const here = indexOfStep(step)
  const current = STEPS[here]

  const goTo = (next: StepKey, forClient?: string) => {
    setSearch(forClient === undefined ? { step: next } : { step: next, client: forClient })
  }

  const onward = () => {
    const next = nextStep(step)
    if (next !== null) goTo(next)
  }

  return (
    <>
      <PageTitle
        eyebrow="Studio · Clients"
        sub="Five steps, each one saved on its own"
        title="New client"
      />

      <Region>
        <Stepper aria-label="Onboarding">
          {STEPS.map((one, index) => (
            <Step key={one.key} state={index < here ? 'done' : index === here ? 'now' : undefined}>
              {one.label}
            </Step>
          ))}
        </Stepper>
        <Small>
          <Muted>
            Each step is one studio action and commits on its own — stopping here is safe, and this
            page is a link you can come back to.
            {client === undefined ? null : (
              <>
                {' '}
                Building <Mono>{client}</Mono>.
              </>
            )}
          </Muted>
        </Small>
      </Region>

      <Section>
        <RegionHead title={current?.title ?? 'New client'} />
        {needsClient(step) && client === undefined ? (
          <NoClientYet
            onStart={() => {
              goTo('details')
            }}
          />
        ) : (
          <StepBody client={client} goTo={goTo} onward={onward} step={step} />
        )}
      </Section>
    </>
  )
}

/* ------------------------------------------------------------------ the steps */

function StepBody({
  step,
  client,
  onward,
  goTo,
}: {
  readonly step: StepKey
  readonly client: string | undefined
  readonly onward: () => void
  readonly goTo: (next: StepKey, forClient?: string) => void
}) {
  if (step === 'details') {
    return (
      <DetailsStep
        onCreated={(slug) => {
          goTo('terms', slug)
        }}
      />
    )
  }

  /* `needsClient` has already answered for every branch below. */
  if (client === undefined) return null

  switch (step) {
    case 'terms':
      return <TermsStep client={client} onNext={onward} />
    case 'owner':
      return <OwnerStep client={client} onNext={onward} />
    case 'package':
      return <PackageStep client={client} onStarted={onward} />
    case 'payment':
      return (
        <PaymentStep
          client={client}
          onBack={() => {
            goTo('package')
          }}
        />
      )
  }
}

function NoClientYet({ onStart }: { readonly onStart: () => void }) {
  return (
    <Stack gap="loose">
      <Notice need role="alert">
        This step is about an account that does not exist yet.
      </Notice>
      <Prose>
        Every step after the first needs a client, and the client is what the first step makes. Open
        the account and the wizard will carry its slug from there.
      </Prose>
      <ButtonRow>
        <Button onClick={onStart} variant="primary">
          Start at the beginning
        </Button>
      </ButtonRow>
    </Stack>
  )
}
